import React from "react";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  ArrowDropDown,
  ArrowDropUp,
  ArrowForward,
  Edit,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CustomAutoComplete, CustomButton } from "components/CustomComponents";
import { themes, chipColors } from "services/constants";
import { boxStyle } from "../ShiftCategoryOptions";

export const WorkAdjustmentPriority = ({
  watch,
  setValue,
  absenceWorkAdjustmentRows,
  setAbsenceWorkAdjustmentRows,
  openAbsenceWorkAdjustmentDialog,
  setOpenAbsenceWorkAdjustmentDialog,
  editingAbsenceWorkAdjustmentIndex,
  absenceWorkAdjustmentDraft,
  absenceWorkAdjustmentErrors,
  absenceWorkAdjustmentBandOptions,
  getAvailablePenaltyOptionsForDraft,
  openAddAbsenceWorkAdjustmentDialog,
  openEditAbsenceWorkAdjustmentDialog,
  handleMoveAbsenceWorkAdjustmentRow,
  handleDeleteAbsenceWorkAdjustmentRow,
  handleAbsenceWorkAdjustmentBandChange,
  handleAddPenaltyToAbsenceDraft,
  handleMoveAbsenceDraftPriority,
  handleRemoveAbsenceDraftPriority,
  handleSaveAbsenceWorkAdjustmentDraft,
}) => {
  const isDefaultBandRow = (row) =>
    ["attendance", "shift earning"].includes(
      (row?.band_name || "").trim().toLowerCase(),
    );

  const getBandDisplayLabel = (row) =>
    isDefaultBandRow(row) ? "Shift Earning" : row.band_name || row.factor;

  return (
  <>
    <Box sx={boxStyle(watch("is_work_adjustment_priority"))}>
      <Typography fontWeight="medium">Work Adjustment Priority</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Switch
          checked={watch("is_work_adjustment_priority")}
          onChange={(event) => {
            const checked = event.target.checked;

            setValue("is_work_adjustment_priority", checked);

            if (!checked) {
              setAbsenceWorkAdjustmentRows([]);
            }
          }}
        />
      </Box>
    </Box>
    {watch("is_work_adjustment_priority") && (
      <Box sx={{ pt: 1 }}>
        <Box sx={{ ml: 2, mb: 2 }}>
          <Box>
            <Typography fontWeight="medium">Adjustment Priority</Typography>
            <Typography variant="body2" color="text.secondary">
              Penalties are deducted from earnings bands in the configured
              order. Each band and its assigned penalties are processed one by
              one as listed.
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={openAddAbsenceWorkAdjustmentDialog}
              disabled={!watch("is_work_adjustment_priority")}
              sx={{
                minHeight: 38,
                px: 2,
                borderRadius: "10px",
                border: "1px dashed #7BC9A7",
                backgroundColor: "#F8FCFA",
                color: "#1A5C42",
                fontSize: "inherit",
                fontWeight: "inherit",
                cursor: "pointer",
                textAlign: "left",
                whiteSpace: "nowrap",
                width: "fit-content",
                maxWidth: "100%",
                opacity: watch("is_work_adjustment_priority") ? 1 : 0.6,
                "&:hover": {
                  backgroundColor: "#F1FAF5",
                  borderColor: "#4FB489",
                },
              }}
            >
              <Typography
                fontWeight={600}
                sx={{ color: "#1A5C42", fontSize: 14, lineHeight: 1.25 }}
              >
                + Add Band for Priority Deduction
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "56px 140px 1fr 80px",
              },
              borderBottom: "1px solid #E7EFEA",
              backgroundColor: "#f5f5fd",
            }}
          >
            <Box />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography fontWeight="medium">Band</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography fontWeight="medium">
                Penalty Priority Assignment
              </Typography>
            </Box>
            <Box />
          </Box>
          {absenceWorkAdjustmentRows.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
              <Typography
                sx={{ fontSize: 13, color: "#8AA695", fontStyle: "italic" }}
              >
                No band priorities have been added yet.
              </Typography>
            </Box>
          )}
          {absenceWorkAdjustmentRows.map((row, index) => (
            <Box
              key={`${row.band_id}-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "56px 140px 1fr 80px",
                },
                alignItems: "center",
                backgroundColor: "transparent",
                borderBottom: "1px solid #EAF0EC",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  px: 1,
                  py: 1.5,
                  borderRight: { md: "1px solid #EAF0EC" },
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}
                >
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() =>
                      handleMoveAbsenceWorkAdjustmentRow(index, -1)
                    }
                    sx={{
                      width: 24,
                      height: 24,
                      border: "1px solid #CFE3D7",
                      borderRadius: "5px",
                      color: "#7A9589",
                      p: 0,
                    }}
                  >
                    <ArrowDropUp sx={{ fontSize: 30 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === absenceWorkAdjustmentRows.length - 1}
                    onClick={() => handleMoveAbsenceWorkAdjustmentRow(index, 1)}
                    sx={{
                      width: 24,
                      height: 24,
                      border: "1px solid #CFE3D7",
                      borderRadius: "5px",
                      color: "#7A9589",
                      p: 0,
                    }}
                  >
                    <ArrowDropDown sx={{ fontSize: 30 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1.75,
                  borderRight: { md: "1px solid #EAF0EC" },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#17372A",
                      lineHeight: 1.1,
                    }}
                  >
                    {getBandDisplayLabel(row)}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  borderRight: { md: "1px solid #EAF0EC" },
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {row.priorities.length > 0 ? (
                    row.priorities.map((priority, priorityIndex) => (
                      <Chip
                        key={`${priority.name}-${priorityIndex}`}
                        size="small"
                        label={
                          <Box component="span">
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: 11,
                                color:
                                  chipColors[priority.name]?.text || "#2C2C2C",
                              }}
                            >
                              P{priorityIndex + 1}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color:
                                  chipColors[priority.name]?.text || "#2C2C2C",
                              }}
                            >
                              {` - ${priority.name}`}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          height: 26,
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          border: "1px solid #C8D8D0",
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#B2D4C0",
                        fontStyle: "italic",
                      }}
                    >
                      No penalties assigned
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => openEditAbsenceWorkAdjustmentDialog(index)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    border: "1px solid #F7D5D5",
                    backgroundColor: "#FFFFFF",
                    color: themes?.greenColor,
                    "&.Mui-disabled": {
                      color: "#98A2B3",
                      borderColor: "#D0D5DD",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  <Edit sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteAbsenceWorkAdjustmentRow(index)}
                  disabled={isDefaultBandRow(row)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    border: "1px solid #F7D5D5",
                    backgroundColor: "#FFFFFF",
                    color: "#E46A6A",
                    "&.Mui-disabled": {
                      color: "#98A2B3",
                      borderColor: "#D0D5DD",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    )}

    <Dialog
      open={openAbsenceWorkAdjustmentDialog}
      onClose={() => setOpenAbsenceWorkAdjustmentDialog(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          border: "1px solid #D6E6DD",
          boxShadow: "0 24px 50px rgba(20, 53, 39, 0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #EAF0EC",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          background: "linear-gradient(180deg, #FCFEFD 0%, #F7FBF9 100%)",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {editingAbsenceWorkAdjustmentIndex === null
              ? "Add Band for Priority Deduction"
              : "Configure Penalty Deduction Priority"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Select an earnings band and assign penalties in deduction order
          </Typography>
        </Box>
        <IconButton
          onClick={() => setOpenAbsenceWorkAdjustmentDialog(false)}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "10px",
            border: "1px solid #D0E8DA",
            color: "#5A7A6A",
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight="medium">Select Earnings Band</Typography>
              <Typography variant="body2" color="text.secondary">
                {isDefaultBandRow(absenceWorkAdjustmentDraft)
                  ? "Shift Earning is the default band and can't be changed; you can still edit its penalty priorities."
                  : "Choose the earnings band to configure penalty deduction priority."}
              </Typography>
            </Box>
            <CustomAutoComplete
              dataList={
                isDefaultBandRow(absenceWorkAdjustmentDraft)
                  ? absenceWorkAdjustmentBandOptions.filter(
                      (option) =>
                        String(option.id) ===
                        String(absenceWorkAdjustmentDraft.band_id),
                    )
                  : absenceWorkAdjustmentBandOptions.filter(
                      (option) =>
                        String(option.id) ===
                          String(absenceWorkAdjustmentDraft.band_id) ||
                        !absenceWorkAdjustmentRows.some(
                          (row, rowIndex) =>
                            String(row.band_id) === String(option.id) &&
                            rowIndex !== editingAbsenceWorkAdjustmentIndex,
                        ),
                    )
              }
              selectedValue={
                absenceWorkAdjustmentDraft.band_id
                  ? {
                      id: String(absenceWorkAdjustmentDraft.band_id),
                      label: absenceWorkAdjustmentDraft.band_name,
                      band_name: absenceWorkAdjustmentDraft.band_name,
                      factor: absenceWorkAdjustmentDraft.factor,
                      source: absenceWorkAdjustmentDraft.source,
                    }
                  : null
              }
              updateDataFunction={(event, selected) =>
                handleAbsenceWorkAdjustmentBandChange(selected)
              }
              placeHolder="Select a band"
              errorMessage={
                absenceWorkAdjustmentErrors.band_id ? "Select a band" : ""
              }
              textBoxError={absenceWorkAdjustmentErrors.band_id}
              disableAction={isDefaultBandRow(absenceWorkAdjustmentDraft)}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 40px 1fr" },
              gap: 2.5,
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Typography fontWeight="medium">Available Penalties</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a penalty to add
              </Typography>

              <Box
                sx={{
                  minHeight: 250,
                  height: "100%",
                  backgroundColor: "#F8FCFA",
                  border: "1px solid #CFE3D7",
                  borderRadius: "12px",
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {getAvailablePenaltyOptionsForDraft(
                  absenceWorkAdjustmentDraft.priorities,
                ).map((option) => (
                  <Box
                    key={option.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #D7E6DE",
                      borderRadius: "10px",
                      p: 1.1,
                      backgroundColor: "#fff",
                      cursor: "pointer",
                    }}
                    onClick={() => handleAddPenaltyToAbsenceDraft(option)}
                  >
                    <Box>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, color: "#2C2C2C" }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={option.type}
                        size="small"
                        sx={{
                          backgroundColor:
                            chipColors[option.label]?.bg || "#EAF4EE",
                          color: chipColors[option.label]?.text || "#6D8A79",
                          "& .MuiChip-label": {
                            px: 1,
                            fontSize: 10,
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddPenaltyToAbsenceDraft(option);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          color: "#1A5C42",
                          borderRadius: "8px",
                        }}
                      >
                        <ArrowForward sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
                {getAvailablePenaltyOptionsForDraft(
                  absenceWorkAdjustmentDraft.priorities,
                ).length === 0 && (
                  <Typography
                    sx={{
                      textAlign: "center",
                      py: 7,
                      fontSize: 13,
                      color: "#8AA695",
                      fontStyle: "italic",
                    }}
                  >
                    All penalties already added.
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                color: "#B2D4C0",
                pt: 5.5,
              }}
            >
              →
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Typography fontWeight="medium">
                Deduction Priority Order
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reorder or remove penalties
              </Typography>
              <Box
                sx={{
                  minHeight: 250,
                  height: "100%",
                  backgroundColor: "#F8FCFA",
                  border: "1px solid #CFE3D7",
                  borderRadius: "12px",
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {absenceWorkAdjustmentDraft.priorities.map(
                  (priority, index) => (
                    <Box
                      key={`${priority.name}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #D7E6DE",
                        borderRadius: "10px",
                        p: 1.1,
                        gap: 1,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "#1A5C42",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#2C2C2C",
                            }}
                          >
                            {priority.name}
                          </Typography>
                          {priority.type ? (
                            <Chip
                              label={priority.type}
                              size="small"
                              sx={{
                                backgroundColor:
                                  chipColors[priority.name]?.bg || "#EAF4EE",
                                color:
                                  chipColors[priority.name]?.text || "#6D8A79",
                                "& .MuiChip-label": {
                                  px: 1,
                                  fontSize: 10,
                                },
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() =>
                            handleMoveAbsenceDraftPriority(index, -1)
                          }
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={
                            index ===
                            absenceWorkAdjustmentDraft.priorities.length - 1
                          }
                          onClick={() =>
                            handleMoveAbsenceDraftPriority(index, 1)
                          }
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveAbsenceDraftPriority(index)
                          }
                          sx={{ color: "#f44336" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ),
                )}
                {absenceWorkAdjustmentDraft.priorities.length === 0 && (
                  <Typography
                    sx={{
                      textAlign: "center",
                      py: 7,
                      fontSize: 13,
                      color: "#8AA695",
                      fontStyle: "italic",
                    }}
                  >
                    No penalties added yet
                  </Typography>
                )}
              </Box>
              {absenceWorkAdjustmentErrors.priorities && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.75, display: "block" }}
                >
                  Add at least one penalty.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #EAF0EC",
          backgroundColor: "#FCFEFD",
        }}
      >
        <CustomButton
          actionName="Cancel"
          typeName="button"
          actionFuntion={() => setOpenAbsenceWorkAdjustmentDialog(false)}
        />
        <CustomButton
          actionName={
            editingAbsenceWorkAdjustmentIndex === null ? "Add Band" : "Save"
          }
          typeName="button"
          actionFuntion={handleSaveAbsenceWorkAdjustmentDraft}
        />
      </DialogActions>
    </Dialog>
  </>
  );
};
